import { useEffect, useRef } from "react";
import type { PropertyResponseType } from "@services/housien/types";
import { backendPropertiesCrud } from "@services/housien";
import {
  createInfiniteScrollGate,
  markNextPageRequested,
  resetInfiniteScrollGate,
  resolvePagedTotal,
  shouldRequestNextPage,
} from "@/shared/utils/infinite-scroll-gate";
import { buildAdvancedFilterQuery } from "./manage-properties-filters";

/**
 * Read-only slice of pagination settings needed by the fetch helpers.
 */
export type FetchSettings = { pageSize: number; page?: number } | undefined;

/**
 * Callbacks the fetch routine uses to publish loading and result state.
 */
export type FetchCallbacks = {
  setIsLoadingMore: (v: boolean) => void;
  setIsLoading: (v: boolean) => void;
  setLoadError: (v: boolean) => void;
  addRequestLoading: () => void;
  removeRequestLoading: () => void;
  setTotalPages: (n: number) => void;
  setTotalCount: (n: number | null) => void;
  setProperties: (updater: PropertyResponseType[] | ((prev: PropertyResponseType[]) => PropertyResponseType[])) => void;
};

/**
 * Inputs required to build the property search request.
 */
export type FetchInputs = {
  agencyId: string | undefined;
  propertiesFilters: Record<string, any>;
  advancedFilters: Record<string, any>;
  fetchSettings: FetchSettings;
  /** Server-side ordering; omitted keys mean the backend's default order. */
  sort?: { order_by: string; order: 'asc' | 'desc' } | null;
  /** Which cover size the rows carry; see `SearchParams.cover`. */
  cover?: 'card';
};

/**
 * Requests one page of properties from the backend for the given filters.
 */
async function requestPage(inputs: FetchInputs) {
  const advancedQuery = buildAdvancedFilterQuery(inputs.advancedFilters);
  const mergedFilters = { ...inputs.propertiesFilters, ...advancedQuery };
  return backendPropertiesCrud(inputs.agencyId as string).search({
    pagination: {
      page: inputs.fetchSettings?.page ?? 1,
      limit: inputs.fetchSettings?.pageSize ?? 10,
    },
    filters: mergedFilters,
    order_by: inputs.sort?.order_by,
    order: inputs.sort?.order,
    cover: inputs.cover,
  });
}

/**
 * Applies a fetched page to state, appending when loading more.
 */
function applyPage(
  cb: FetchCallbacks,
  isLoadMore: boolean,
  realEstateProperties: unknown,
) {
  if (realEstateProperties) {
    const next = Object.values(realEstateProperties) as PropertyResponseType[];
    cb.setProperties((prev) => (isLoadMore ? [...prev, ...next] : next));
    return;
  }
  if (!isLoadMore) cb.setProperties([]);
}

/**
 * Toggles the loading flags for the start of a fetch.
 */
function beginFetch(cb: FetchCallbacks, isLoadMore: boolean) {
  if (isLoadMore) {
    cb.setIsLoadingMore(true);
    return;
  }
  cb.setIsLoading(true);
  cb.setLoadError(false);
  cb.addRequestLoading();
}

/**
 * Toggles the loading flags for the end of a fetch.
 */
function endFetch(cb: FetchCallbacks, isLoadMore: boolean) {
  if (isLoadMore) {
    cb.setIsLoadingMore(false);
    return;
  }
  cb.setIsLoading(false);
  cb.removeRequestLoading();
}

/**
 * Publishes page items and the resolved total page count.
 *
 * @param inputs - Fetch inputs (page / page size).
 * @param cb - State callbacks.
 * @param isLoadMore - Whether this page appends to the list.
 * @param data - Search payload.
 * @param pages - Backend-reported page count.
 * @param totalCount - Backend-reported total matching properties, if present.
 */
function publishFetchedPage(
  inputs: FetchInputs,
  cb: FetchCallbacks,
  isLoadMore: boolean,
  data: unknown,
  pages: number,
  totalCount: number | null,
) {
  const itemCount = data ? Object.values(data as object).length : 0;
  const page = inputs.fetchSettings?.page ?? 1;
  const pageSize = inputs.fetchSettings?.pageSize ?? 10;
  cb.setTotalPages(resolvePagedTotal(page, pageSize, itemCount, pages));
  cb.setTotalCount(totalCount);
  applyPage(cb, isLoadMore, data);
}

/**
 * Fetches properties and publishes the result through the given callbacks.
 */
export async function fetchProperties(
  inputs: FetchInputs,
  cb: FetchCallbacks,
  isLoadMore = false,
) {
  // Guarded before beginFetch, not inside the try: returning from within it
  // still ran the finally, so endFetch cleared isLoading on a mount where the
  // agency id had not resolved yet. The list then rendered "Seu portfólio está
  // vazio" over an agency with 50 properties, until the real fetch landed.
  if (!inputs.agencyId) return;
  try {
    beginFetch(cb, isLoadMore);
    const { data, metadata } = await requestPage(inputs);
    const rawTotal = metadata?.statistics?.total_properties;
    const totalCount = typeof rawTotal === 'number' ? rawTotal : null;
    publishFetchedPage(inputs, cb, isLoadMore, data, metadata?.pages ?? 1, totalCount);
  } catch {
    if (!isLoadMore) cb.setLoadError(true);
  } finally {
    endFetch(cb, isLoadMore);
  }
}

/**
 * Builds a stable key describing the current filter/pagination inputs.
 */
export function buildFilterKey(
  fetchId: unknown,
  agencyId: string | undefined,
  propertiesFilters: Record<string, any>,
  advancedFilters: Record<string, any>,
  sort?: { order_by: string; order: 'asc' | 'desc' } | null,
  cover?: 'card',
): string {
  const sortKey = sort ? `${sort.order_by}:${sort.order}` : 'default';
  // The cover size is part of the key: rows fetched for the list carry no
  // card cover, so switching layout has to start over from page 1.
  return `${fetchId}|${agencyId}|${JSON.stringify(propertiesFilters)}|${JSON.stringify(advancedFilters)}|${sortKey}|${cover ?? 'row'}`;
}

/**
 * Inputs the fetch effect watches to decide when and how to fetch.
 */
export type FetchEffectInputs = {
  fetchId: unknown;
  agencyId: string | undefined;
  propertiesFilters: Record<string, any>;
  advancedFilters: Record<string, any>;
  fetchSettings: FetchSettings;
  setFetchSettings: (s: { pageSize: number; page?: number }) => void;
  sort?: { order_by: string; order: 'asc' | 'desc' } | null;
  cover?: 'card';
};

/**
 * Resets to page 1 when filters change on a paginated view; returns true if it did.
 */
function resetPageOnFilterChange(
  inputs: FetchEffectInputs,
  filterChanged: boolean,
): boolean {
  const page = inputs.fetchSettings?.page ?? 1;
  if (filterChanged && page > 1) {
    inputs.setFetchSettings({ ...inputs.fetchSettings!, page: 1 });
    return true;
  }
  return false;
}

/**
 * Runs the property fetch whenever filters, agency, or pagination change.
 */
export function useFetchPropertiesEffect(inputs: FetchEffectInputs, cb: FetchCallbacks) {
  const prevFilterKeyRef = useRef('');
  const { fetchId, agencyId, propertiesFilters, advancedFilters, fetchSettings, sort, cover } = inputs;
  useEffect(() => {
    const filterKey = buildFilterKey(fetchId, agencyId, propertiesFilters, advancedFilters, sort, cover);
    const filterChanged = prevFilterKeyRef.current !== '' && prevFilterKeyRef.current !== filterKey;
    prevFilterKeyRef.current = filterKey;
    if (resetPageOnFilterChange(inputs, filterChanged)) return;
    const page = fetchSettings?.page ?? 1;
    fetchProperties(
      { agencyId, propertiesFilters, advancedFilters, fetchSettings, sort, cover },
      cb,
      page > 1 && !filterChanged,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchId, agencyId, propertiesFilters, advancedFilters, fetchSettings, sort, cover]);
}

/**
 * Parameters that drive the infinite-scroll observer.
 */
export type InfiniteScrollParams = {
  sentinelRef: React.RefObject<HTMLDivElement | null>;
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  isMobile: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
  totalPages: number;
  fetchSettings: FetchSettings;
  setFetchSettings: (s: { pageSize: number; page?: number }) => void;
};

/**
 * Builds the IntersectionObserver callback that advances pagination.
 *
 * @param params - Infinite-scroll bindings.
 * @param gate - Session lock shared across observer reconnects.
 * @returns An observer entry handler bound to the given params.
 */
function makeScrollObserverCallback(
  params: InfiniteScrollParams,
  gate: ReturnType<typeof createInfiniteScrollGate>,
): IntersectionObserverCallback {
  return ([entry]) => {
    const page = params.fetchSettings?.page ?? 1;
    const idle = !params.isLoading && !params.isLoadingMore;
    const hasMore = page < params.totalPages;
    if (!shouldRequestNextPage(entry.isIntersecting, idle, hasMore, gate)) return;
    markNextPageRequested(gate);
    params.setFetchSettings({ ...params.fetchSettings!, page: page + 1 });
  };
}

/**
 * Observes the sentinel element and advances the page when it becomes visible.
 */
export function useInfiniteScroll(params: InfiniteScrollParams) {
  const {
    sentinelRef, scrollContainerRef, isMobile, isLoading,
    isLoadingMore, totalPages, fetchSettings, setFetchSettings,
  } = params;
  const gateRef = useRef(createInfiniteScrollGate());
  const page = fetchSettings?.page ?? 1;
  useEffect(() => {
    if (page === 1) resetInfiniteScrollGate(gateRef.current);
  }, [page]);
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const root = isMobile ? null : scrollContainerRef.current;
    const observer = new IntersectionObserver(
      makeScrollObserverCallback(params, gateRef.current),
      { root, threshold: 0.1 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isLoadingMore, fetchSettings, totalPages, setFetchSettings, isMobile]);
}
